import { apiFetch } from "./client";

export type KpiKind = "SALES" | "OTKUP";

export type KpiTargetView = {
  id: string;
  userId: string;
  periodYear: number;
  periodMonth: number;
  targetRevenueEur: string;
  kind: KpiKind;
  /** EUR for sales, T (tonnes) for procurement. The screen reads this, not the field name. */
  unit: "EUR" | "T";
  note: string | null;
};

/** The buy-in desk's season, in tonnes. Deliberately not the same shape as AnnualPerformance. */
export type OtkupPerformance = {
  userId: string;
  userName: string;
  year: number;
  target: string;
  actual: string;
  unit: "T";
  attainment: number | null;
  supplierCount: number;
};

export type MonthlyPerformance = {
  year: number;
  month: number;
  targetRevenueEur: string | null;
  /**
   * Invoiced orders in the month. Empty for a month after the day the invoices reach: it has no
   * figure yet, and the screen writes a dash rather than a zero.
   */
  invoicedOrderCount: number | null;
  actualRevenueEur: string | null;
  attainment: number | null;
};

export type AnnualPerformance = {
  userId: string;
  userName: string;
  role: "SALES_DIRECTOR" | "SALES";
  year: number;
  totalTargetEur: string;
  totalInvoicedOrders: number;
  totalActualEur: string;
  months: MonthlyPerformance[];
};

export type MyPerformance = {
  year: number;
  ytdTargetEur: string;
  ytdInvoicedOrders: number;
  ytdActualEur: string;
  ytdAttainment: number | null;
  months: MonthlyPerformance[];
};

export const kpiApi = {
  me: (year?: number) =>
    apiFetch<MyPerformance>(`/kpi/me${year ? `?year=${year}` : ""}`),
  userPerformance: (userId: string, year?: number) =>
    apiFetch<AnnualPerformance>(`/kpi/users/${userId}${year ? `?year=${year}` : ""}`),
  team: (year?: number) =>
    apiFetch<AnnualPerformance[]>(`/kpi/team${year ? `?year=${year}` : ""}`),
  targets: (userId: string, year?: number) =>
    apiFetch<KpiTargetView[]>(`/kpi/targets/${userId}${year ? `?year=${year}` : ""}`),
  otkupPerformance: (userId: string, year?: number) =>
    apiFetch<OtkupPerformance>(`/kpi/otkup/${userId}${year ? `?year=${year}` : ""}`),
  otkupTeam: (year?: number) =>
    apiFetch<OtkupPerformance[]>(`/kpi/otkup-team${year ? `?year=${year}` : ""}`),
  setTarget: (
    userId: string,
    year: number,
    month: number,
    body: { targetRevenueEur: number; kind?: KpiKind; note?: string },
  ) =>
    apiFetch<KpiTargetView>(`/kpi/targets/${userId}/${year}/${month}`, {
      method: "PUT",
      body,
    }),
};
